import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  TextField,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  ColorLens as ColorIcon,
  TextFields as TypographyIcon,
  CropOriginal as ImageIcon,
  Palette as DesignIcon,
  Save as SaveIcon,
  Link as LinkIcon,
  AccountTree as FlowIcon,
  Image as GalleryIcon,
  InsertComment as CommentIcon,
  ArrowForward as ArrowRightIcon
} from '@mui/icons-material';
import WorkflowChat from '../WorkflowChat';
import GrootWorkflowChat from '../GrootWorkflowChat';
import useDocuments from '../../../hooks/useDocuments';
import useNotification from '../../../hooks/useNotification';
import useFeature from '../../../hooks/useFeature';

const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 800;

const Designs = ({ projectId, onSave, activeTab: initialActiveTab }) => {
  const [activeTab, setActiveTab] = useState(initialActiveTab || 0);
  const [designs, setDesigns] = useState([{ 
    id: 'design-1', 
    name: 'Homepage', 
    description: 'Main landing page design',
    imageUrl: null,
    htmlPreview: null,
    featureId: null,
    designSystem: {
      colors: {
        primary: '#1976d2',
        secondary: '#dc004e',
        background: '#ffffff',
        text: '#333333'
      },
      typography: {
        fontFamily: 'Roboto, sans-serif',
        headingSize: '24px',
        bodySize: '16px'
      }
    }
  }]);
  const [activeDesignIndex, setActiveDesignIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [features, setFeatures] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({ title: '', content: '' });
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadType, setUploadType] = useState('');
  const [designLink, setDesignLink] = useState('');
  const [artifactTitle, setArtifactTitle] = useState('');
  const [artifactDescription, setArtifactDescription] = useState('');
  const [artifacts, setArtifacts] = useState([
    // Example artifacts that will be populated from uploaded designs
  ]);
  const [designSystem, setDesignSystem] = useState({
    colors: {
      primary: '#1976d2',
      secondary: '#dc004e',
      background: '#ffffff',
      text: '#333333',
      accent: '#ff9800'
    },
    typography: {
      fontFamily: 'Roboto, sans-serif',
      headingSize: '24px',
      bodySize: '16px',
      buttonSize: '14px'
    },
    spacing: {
      base: '8px',
      small: '4px',
      medium: '16px',
      large: '24px'
    },
    borderRadius: {
      small: '4px',
      medium: '8px',
      large: '16px'
    }
  });
  
  const fileInputRef = useRef(null);
  
  const { showNotification } = useNotification();
  const { createDocument } = useDocuments();
  const { getFeaturesByProject } = useFeature();
  
  // Load project features
  useEffect(() => {
    if (projectId) {
      const loadFeatures = async () => {
        try {
          const projectFeatures = await getFeaturesByProject(projectId);
          setFeatures(projectFeatures);
        } catch (error) {
          console.error('Error loading features:', error);
        }
      };
      
      loadFeatures();
    }
  }, [projectId, getFeaturesByProject]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddDesign = () => {
    const newDesign = {
      id: `design-${designs.length + 1}`,
      name: `New Design ${designs.length + 1}`,
      description: 'Design description',
      imageUrl: null,
      featureId: null,
      designSystem: { ...designSystem }
    };
    
    setDesigns([...designs, newDesign]);
    setActiveDesignIndex(designs.length);
  };

  const handleDeleteDesign = (index) => {
    if (designs.length > 1) {
      const updatedDesigns = designs.filter((_, i) => i !== index);
      setDesigns(updatedDesigns);
      
      // Adjust active design index if needed
      if (activeDesignIndex >= index) {
        setActiveDesignIndex(Math.max(0, activeDesignIndex - 1));
      }
    } else {
      showNotification({
        message: 'Cannot delete the only design',
        type: 'info'
      });
    }
  };
  
  const handleOpenUploadDialog = (type) => {
    setUploadType(type);
    setArtifactTitle('');
    setArtifactDescription('');
    setDesignLink('');
    setUploadDialogOpen(true);
  };
  
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
  };
  
  const handleUploadArtifact = () => {
    // Create a new artifact based on upload type
    const newArtifact = {
      id: `artifact-${Date.now()}`,
      title: artifactTitle || `Design Artifact ${artifacts.length + 1}`,
      description: artifactDescription || 'No description provided',
      createdAt: new Date().toISOString(),
      type: uploadType,
    };
    
    // Add type-specific properties
    if (uploadType === 'link') {
      newArtifact.link = designLink;
      newArtifact.thumbnail = 'https://placeholder.pics/svg/300x200/DEDEDE/555555/Link%20Preview';
    } else if (uploadType === 'pdf') {
      // For PDF uploads - file is handled by fileInputRef
      if (fileInputRef.current && fileInputRef.current.files[0]) {
        const file = fileInputRef.current.files[0];
        const reader = new FileReader();
        
        reader.onload = (e) => {
          // Create a new artifacts array with the PDF data
          const updatedArtifacts = [...artifacts, {
            ...newArtifact,
            fileData: e.target.result,
            fileName: file.name,
            fileType: file.type,
            thumbnail: 'https://placeholder.pics/svg/300x200/DEDEDE/555555/PDF%20Document'
          }];
          
          setArtifacts(updatedArtifacts);
          
          // Also update designs array with the new PDF as a display
          const updatedDesigns = [...designs];
          updatedDesigns[activeDesignIndex].artifact = newArtifact.id;
          setDesigns(updatedDesigns);
          
          showNotification({
            message: 'Design document uploaded successfully',
            type: 'success'
          });
        };
        
        reader.readAsDataURL(file);
      }
      
      handleCloseUploadDialog();
      return; // Early return since we're handling PDF async
    } else if (uploadType === 'image') {
      // For image uploads
      if (fileInputRef.current && fileInputRef.current.files[0]) {
        const file = fileInputRef.current.files[0];
        const reader = new FileReader();
        
        reader.onload = (e) => {
          // Update the designs array with the image
          const updatedDesigns = [...designs];
          updatedDesigns[activeDesignIndex].imageUrl = e.target.result;
          setDesigns(updatedDesigns);
          
          // Create a new artifacts array with the image data
          const updatedArtifacts = [...artifacts, {
            ...newArtifact,
            imageUrl: e.target.result,
            fileName: file.name,
            fileType: file.type
          }];
          
          setArtifacts(updatedArtifacts);
          
          showNotification({
            message: 'Design image uploaded successfully',
            type: 'success'
          });
        };
        
        reader.readAsDataURL(file);
      }
      
      handleCloseUploadDialog();
      return; // Early return since we're handling image async
    }
    
    // For links or other types that don't need async processing
    const updatedArtifacts = [...artifacts, newArtifact];
    setArtifacts(updatedArtifacts);
    
    showNotification({
      message: 'Design artifact added successfully',
      type: 'success'
    });
    
    handleCloseUploadDialog();
  };

  const handleUpdateDesign = (index, field, value) => {
    const updatedDesigns = [...designs];
    updatedDesigns[index][field] = value;
    setDesigns(updatedDesigns);
  };

  const handleUpdateDesignSystem = (category, property, value) => {
    const updatedDesignSystem = { ...designSystem };
    updatedDesignSystem[category][property] = value;
    setDesignSystem(updatedDesignSystem);
    
    // Also update current design
    const updatedDesigns = [...designs];
    updatedDesigns[activeDesignIndex].designSystem = { ...updatedDesignSystem };
    setDesigns(updatedDesigns);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const updatedDesigns = [...designs];
        updatedDesigns[activeDesignIndex].imageUrl = e.target.result;
        setDesigns(updatedDesigns);
        
        showNotification({
          message: 'Design image uploaded successfully',
          type: 'success'
        });
      };
      
      reader.readAsDataURL(file);
    }
  };

  const saveDesigns = async () => {
    try {
      setLoading(true);
      
      // Prepare designs data for saving, including artifacts
      const designsData = {
        designs: designs,
        designSystem: designSystem,
        artifacts: artifacts
      };
      
      // Save designs as document
      if (projectId) {
        const document = await createDocument(projectId, {
          title: 'Project Designs',
          content: JSON.stringify(designsData, null, 2),
          type: 'designs'
        });
        
        showNotification({
          message: 'Designs saved successfully',
          type: 'success'
        });
        
        // Notify parent component
        if (onSave) {
          onSave(JSON.stringify(designsData, null, 2));
        }
      }
    } catch (error) {
      console.error('Error saving designs:', error);
      showNotification({
        message: `Error saving designs: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to create a design artifact from chat
  const createDesignArtifactFromChat = (content) => {
    // Create a new artifact from chat-generated content
    const newArtifact = {
      id: `artifact-${Date.now()}`,
      title: `Design from Chat - ${new Date().toLocaleDateString()}`,
      description: 'Generated from conversation with AI assistant',
      createdAt: new Date().toISOString(),
      type: 'chat',
      content: content,
      thumbnail: 'https://placeholder.pics/svg/300x200/DEDEDE/555555/AI%20Generated'
    };
    
    // Add to artifacts list
    const updatedArtifacts = [...artifacts, newArtifact];
    setArtifacts(updatedArtifacts);
    
    showNotification({
      message: 'Design artifact created from chat',
      type: 'success'
    });
    
    return newArtifact.id;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">UI/UX Designs</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<UploadIcon />}
            onClick={() => handleOpenUploadDialog('link')}
          >
            Add Design Link
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<UploadIcon />}
            onClick={() => handleOpenUploadDialog('pdf')}
          >
            Upload PDF
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<UploadIcon />}
            onClick={() => handleOpenUploadDialog('image')}
          >
            Upload Image
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />}
            onClick={handleAddDesign}
          >
            Add Design
          </Button>
          <Button 
            variant="contained" 
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading}
            onClick={saveDesigns}
          >
            Save Designs
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Designs" />
        <Tab label="Design System" />
        <Tab label="Feature Mapping" />
        <Tab label="Design Artifacts" />
        <Tab icon={<CommentIcon />} label="Chat" />
      </Tabs>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Upload/Design Options Section - Featured prominently at the top */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Upload Designs</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Share your existing designs by uploading images, PDFs or linking to design tools
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Button 
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => handleOpenUploadDialog('link')}
                      sx={{ minWidth: '160px' }}
                    >
                      Add Design Link
                    </Button>
                    <Button 
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => handleOpenUploadDialog('pdf')}
                      sx={{ minWidth: '160px' }}
                    >
                      Upload PDF
                    </Button>
                    <Button 
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() => handleOpenUploadDialog('image')}
                      sx={{ minWidth: '160px' }}
                    >
                      Upload Image
                    </Button>
                  </Box>
                  
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Supported design tools:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label="Figma" size="small" />
                      <Chip label="Adobe XD" size="small" />
                      <Chip label="Sketch" size="small" />
                      <Chip label="InVision" size="small" />
                      <Chip label="PDF Documents" size="small" />
                      <Chip label="Image Files" size="small" />
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Generate with AI</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Chat with our AI to create new designs or get recommendations based on your requirements
                  </Typography>
                  
                  <Box sx={{ height: '100px', mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="Describe the design you need..."
                      variant="standard"
                      InputProps={{
                        endAdornment: (
                          <Button 
                            onClick={() => {
                              setActiveTab(4);
                              // Scroll to chat section
                              setTimeout(() => {
                                document.getElementById('chat-section')?.scrollIntoView({ behavior: 'smooth' });
                              }, 100);
                            }}
                            variant="contained"
                            color="primary"
                            size="small"
                          >
                            Chat with AI
                          </Button>
                        ),
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="text"
                      onClick={() => setActiveTab(3)}
                      endIcon={<ArrowRightIcon />}
                    >
                      View All Design Artifacts
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Design Screens</Typography>
              <Divider sx={{ mb: 2 }} />
              
              {designs.map((design, index) => (
                <Box 
                  key={design.id}
                  sx={{ 
                    mb: 1, 
                    p: 1, 
                    bgcolor: index === activeDesignIndex ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                    borderRadius: 1,
                    border: index === activeDesignIndex ? '1px solid rgba(25, 118, 210, 0.5)' : '1px solid transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                  onClick={() => setActiveDesignIndex(index)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {design.imageUrl ? (
                      <Box 
                        component="img" 
                        src={design.imageUrl}
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          mr: 1,
                          objectFit: 'cover',
                          borderRadius: 1
                        }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          mr: 1, 
                          bgcolor: '#e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1
                        }}
                      >
                        <DesignIcon color="action" fontSize="small" />
                      </Box>
                    )}
                    <Typography variant="body2" noWrap>
                      {design.name}
                    </Typography>
                  </Box>
                  
                  <IconButton 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDesign(index);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={9}>
            {designs[activeDesignIndex] && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <TextField
                    label="Screen Name"
                    value={designs[activeDesignIndex].name}
                    onChange={(e) => handleUpdateDesign(activeDesignIndex, 'name', e.target.value)}
                    size="small"
                    sx={{ width: 250 }}
                  />
                  
                  <Box>
                    <input
                      type="file"
                      id="design-upload"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                      ref={fileInputRef}
                    />
                    <label htmlFor="design-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        onClick={() => fileInputRef.current.click()}
                      >
                        Upload Design
                      </Button>
                    </label>
                  </Box>
                </Box>
                
                <TextField
                  label="Description"
                  value={designs[activeDesignIndex].description}
                  onChange={(e) => handleUpdateDesign(activeDesignIndex, 'description', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  sx={{ mb: 2 }}
                />
                
                {designs[activeDesignIndex].featureId && (
                  <Chip 
                    icon={<LinkIcon />}
                    label={`Linked to: ${features.find(f => f._id === designs[activeDesignIndex].featureId)?.title || 'Unknown Feature'}`}
                    sx={{ mb: 2 }}
                    color="primary"
                    variant="outlined"
                  />
                )}
                
                <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 2, textAlign: 'center', mb: 2 }}>
                  {designs[activeDesignIndex].imageUrl ? (
                    <img 
                      src={designs[activeDesignIndex].imageUrl} 
                      alt={designs[activeDesignIndex].name}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '600px',
                        border: '1px solid #ccc'
                      }}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        height: 400, 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: '#fff',
                        border: '1px dashed #ccc'
                      }}
                    >
                      <GalleryIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No Design Uploaded
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload a design image or create one
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={() => fileInputRef.current.click()}
                      >
                        Upload Design
                      </Button>
                    </Box>
                  )}
                </Box>
                
                <Typography variant="subtitle2" gutterBottom>Design Colors</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {designs[activeDesignIndex].designSystem?.colors && Object.entries(designs[activeDesignIndex].designSystem.colors).map(([key, value]) => (
                    <Box 
                      key={key}
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: value,
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        p: 0.5,
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <Typography variant="caption" sx={{ 
                        color: key === 'background' ? '#000' : '#fff',
                        fontSize: '0.6rem',
                        textShadow: '0px 0px 2px rgba(0,0,0,0.4)',
                        wordBreak: 'break-all'
                      }}>
                        {key}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>Design System</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Color Palette</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {Object.entries(designSystem.colors).map(([key, value]) => (
                    <Grid item xs={6} md={4} key={key}>
                      <Box sx={{ mb: 2 }}>
                        <Box 
                          sx={{ 
                            height: 60, 
                            bgcolor: value, 
                            borderRadius: '4px 4px 0 0',
                            border: '1px solid #e0e0e0',
                            borderBottom: 'none'
                          }} 
                        />
                        <Box 
                          sx={{ 
                            p: 1, 
                            border: '1px solid #e0e0e0',
                            borderTop: 'none',
                            borderRadius: '0 0 4px 4px',
                            bgcolor: '#f9f9f9'
                          }}
                        >
                          <Typography variant="caption" display="block" sx={{ textTransform: 'capitalize' }}>
                            {key}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                              size="small"
                              value={value}
                              onChange={(e) => handleUpdateDesignSystem('colors', key, e.target.value)}
                              sx={{ 
                                '& .MuiInputBase-input': { 
                                  p: 0.5, 
                                  fontSize: '0.75rem',
                                  height: '1.5rem'
                                } 
                              }}
                              fullWidth
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
              
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Typography</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {Object.entries(designSystem.typography).map(([key, value]) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <TextField
                        label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        value={value}
                        onChange={(e) => handleUpdateDesignSystem('typography', key, e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                  ))}
                </Grid>
                
                <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                  <Typography variant="h6" style={{ fontFamily: designSystem.typography.fontFamily, fontSize: designSystem.typography.headingSize }}>
                    Heading Example
                  </Typography>
                  <Typography variant="body1" style={{ fontFamily: designSystem.typography.fontFamily, fontSize: designSystem.typography.bodySize }}>
                    This is an example of body text using the selected typography settings. The text should be legible and match your brand's style guidelines.
                  </Typography>
                  <Button
                    variant="contained"
                    style={{ 
                      backgroundColor: designSystem.colors.primary,
                      color: '#fff',
                      fontFamily: designSystem.typography.fontFamily,
                      fontSize: designSystem.typography.buttonSize,
                      marginTop: '16px'
                    }}
                  >
                    Button Example
                  </Button>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Spacing</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {Object.entries(designSystem.spacing).map(([key, value]) => (
                    <Grid item xs={6} key={key}>
                      <TextField
                        label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        value={value}
                        onChange={(e) => handleUpdateDesignSystem('spacing', key, e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                  ))}
                </Grid>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Spacing Preview</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {Object.entries(designSystem.spacing).map(([key, value]) => (
                      <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ width: 80 }}>
                          {key}: {value}
                        </Typography>
                        <Box
                          sx={{
                            height: '20px',
                            bgcolor: designSystem.colors.primary,
                            width: value,
                            minWidth: '20px',
                            borderRadius: 1
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Paper>
              
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Border Radius</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {Object.entries(designSystem.borderRadius).map(([key, value]) => (
                    <Grid item xs={6} key={key}>
                      <TextField
                        label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        value={value}
                        onChange={(e) => handleUpdateDesignSystem('borderRadius', key, e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                  ))}
                </Grid>
                
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  {Object.entries(designSystem.borderRadius).map(([key, value]) => (
                    <Box
                      key={key}
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: designSystem.colors.primary,
                        borderRadius: value,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#fff' }}>
                        {key}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
              
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Component Preview</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ bgcolor: '#f9f9f9', p: 2, borderRadius: 1 }}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: designSystem.colors.background,
                    border: `1px solid ${designSystem.colors.primary}`,
                    borderRadius: designSystem.borderRadius.medium
                  }}>
                    <Typography variant="h6" style={{ 
                      fontFamily: designSystem.typography.fontFamily,
                      fontSize: designSystem.typography.headingSize,
                      color: designSystem.colors.text,
                      marginBottom: designSystem.spacing.medium
                    }}>
                      Card Component
                    </Typography>
                    
                    <Typography variant="body1" style={{ 
                      fontFamily: designSystem.typography.fontFamily,
                      fontSize: designSystem.typography.bodySize,
                      color: designSystem.colors.text,
                      marginBottom: designSystem.spacing.base
                    }}>
                      This shows how components will look with your design system.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button 
                        variant="contained"
                        style={{
                          backgroundColor: designSystem.colors.primary,
                          color: '#fff',
                          fontFamily: designSystem.typography.fontFamily,
                          fontSize: designSystem.typography.buttonSize,
                          borderRadius: designSystem.borderRadius.small,
                          padding: `${designSystem.spacing.small} ${designSystem.spacing.medium}`
                        }}
                      >
                        Primary
                      </Button>
                      
                      <Button 
                        variant="contained"
                        style={{
                          backgroundColor: designSystem.colors.secondary,
                          color: '#fff',
                          fontFamily: designSystem.typography.fontFamily,
                          fontSize: designSystem.typography.buttonSize,
                          borderRadius: designSystem.borderRadius.small,
                          padding: `${designSystem.spacing.small} ${designSystem.spacing.medium}`
                        }}
                      >
                        Secondary
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Link Designs to Features</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Connect your designs to project features to maintain traceability
              </Typography>
              
              <Grid container spacing={2}>
                {features.length > 0 ? (
                  features.map((feature) => (
                    <Grid item xs={12} key={feature._id}>
                      <Card variant="outlined" sx={{ mb: 1 }}>
                        <CardContent sx={{ pb: '8px !important' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2">{feature.title}</Typography>
                            
                            <Box>
                              {designs.some(d => d.featureId === feature._id) ? (
                                <Chip 
                                  label="Designed" 
                                  size="small" 
                                  color="success" 
                                  variant="outlined" 
                                />
                              ) : (
                                <Chip 
                                  label="No Design" 
                                  size="small" 
                                  color="default" 
                                  variant="outlined" 
                                />
                              )}
                            </Box>
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
                            {feature.description}
                          </Typography>
                          
                          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button 
                              size="small" 
                              startIcon={<LinkIcon />}
                              onClick={() => {
                                // Link the current design to this feature
                                const updatedDesigns = [...designs];
                                updatedDesigns[activeDesignIndex].featureId = feature._id;
                                setDesigns(updatedDesigns);
                                
                                showNotification({
                                  message: `Linked "${designs[activeDesignIndex].name}" to feature "${feature.title}"`,
                                  type: 'success'
                                });
                              }}
                            >
                              Link Current Design
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      No features found for this project
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Linked Designs</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                View designs that are linked to features
              </Typography>
              
              <Grid container spacing={2}>
                {designs.filter(d => d.featureId).length > 0 ? (
                  designs.filter(d => d.featureId).map((design) => {
                    const linkedFeature = features.find(f => f._id === design.featureId);
                    
                    return (
                      <Grid item xs={12} sm={6} key={design.id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 2 }
                          }}
                          onClick={() => {
                            // Switch to the design tab and select this design
                            setActiveTab(0);
                            setActiveDesignIndex(designs.findIndex(d => d.id === design.id));
                          }}
                        >
                          {design.imageUrl && (
                            <CardMedia
                              component="img"
                              height="140"
                              image={design.imageUrl}
                              alt={design.name}
                            />
                          )}
                          
                          <CardContent>
                            <Typography variant="subtitle2">{design.name}</Typography>
                            <Chip 
                              icon={<LinkIcon fontSize="small" />} 
                              label={linkedFeature?.title || 'Unknown Feature'} 
                              size="small"
                              sx={{ mt: 1 }}
                              color="primary"
                              variant="outlined"
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })
                ) : (
                  <Grid item xs={12}>
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <FlowIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                      <Typography variant="body1" sx={{ mt: 2 }}>
                        No linked designs
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Link designs to features to see them here
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>Design Artifacts</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Here you can view all design artifacts that have been uploaded or generated from chats
          </Typography>
          
          <Grid container spacing={3}>
            {artifacts.length > 0 ? (
              artifacts.map((artifact) => (
                <Grid item xs={12} sm={6} md={4} key={artifact.id}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': { boxShadow: 2 } 
                    }}
                  >
                    {artifact.imageUrl ? (
                      <CardMedia
                        component="img"
                        height="180"
                        image={artifact.imageUrl}
                        alt={artifact.title}
                      />
                    ) : (
                      <CardMedia
                        component="img"
                        height="140"
                        image={artifact.thumbnail || "https://placeholder.pics/svg/300x200/DEDEDE/555555/Design%20Artifact"}
                        alt={artifact.title}
                      />
                    )}
                    
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" gutterBottom>{artifact.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {artifact.description}
                      </Typography>
                      
                      <Box sx={{ mt: 2 }}>
                        {artifact.type === 'link' && (
                          <Button 
                            variant="outlined" 
                            size="small" 
                            startIcon={<LinkIcon />}
                            href={artifact.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            fullWidth
                          >
                            View Design
                          </Button>
                        )}
                        
                        {artifact.type === 'pdf' && (
                          <Button 
                            variant="outlined" 
                            size="small" 
                            fullWidth
                            onClick={() => window.open(artifact.fileData)}
                          >
                            Open PDF
                          </Button>
                        )}
                        
                        {artifact.type === 'chat' && (
                          <Button 
                            variant="outlined" 
                            size="small" 
                            fullWidth
                            onClick={() => {
                              setDialogOpen(true);
                              setDialogData({
                                title: artifact.title,
                                content: artifact.content
                              });
                            }}
                          >
                            View Content
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                    
                    <Box 
                      sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1, 
                        bgcolor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {new Date(artifact.createdAt).toLocaleDateString()}
                      </Typography>
                      
                      <Chip
                        label={artifact.type}
                        size="small"
                        color={
                          artifact.type === 'link' ? 'primary' :
                          artifact.type === 'pdf' ? 'secondary' :
                          artifact.type === 'image' ? 'success' : 'default'
                        }
                        variant="outlined"
                      />
                    </Box>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Box 
                  sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Design Artifacts Yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Upload designs, add links or generate designs from chat to create artifacts
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                    <Button 
                      variant="outlined" 
                      startIcon={<UploadIcon />}
                      onClick={() => handleOpenUploadDialog('link')}
                    >
                      Add Link
                    </Button>
                    <Button 
                      variant="outlined" 
                      startIcon={<UploadIcon />}
                      onClick={() => handleOpenUploadDialog('pdf')}
                    >
                      Upload PDF
                    </Button>
                    <Button 
                      variant="outlined" 
                      startIcon={<CommentIcon />}
                      onClick={() => setActiveTab(4)}
                    >
                      Chat to Generate
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
      
      {activeTab === 4 && (
        <Box>
          <Typography variant="h6" gutterBottom>Design Chat</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Chat with AI to generate designs and see real-time previews
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper 
                variant="outlined" 
                sx={{ p: 2, mb: 2, height: '610px' }}
                id="chat-section"
              >
                <Typography variant="subtitle1" gutterBottom>Chat with AI Design Assistant</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Describe your design needs and see the preview in real-time
                </Typography>
                
                <Box sx={{ height: '500px' }}>
                  <WorkflowChat
                    sectionName="Designs"
                    projectId={projectId}
                    placeholder="Describe your design needs in detail..."
                    streamHandler={{
                      onChunk: (chunk, fullText) => {
                        // Update the preview in real-time as streaming occurs
                        const updatedDesigns = [...designs];
                        
                        // Update description with current streaming content
                        updatedDesigns[activeDesignIndex].description = fullText.slice(0, 120) + '...';
                        
                        // Extract HTML code blocks
                        const extractHtml = (text) => {
                          // Look for HTML code blocks in markdown format
                          const htmlRegex = /```html\s*([\s\S]*?)\s*```|<html[\s\S]*?<\/html>|<body[\s\S]*?<\/body>|<div[\s\S]*?<\/div>/gi;
                          const matches = text.match(htmlRegex);
                          
                          if (matches && matches.length > 0) {
                            // Clean up the HTML
                            let html = matches[matches.length - 1]; // Get the last HTML block (most complete)
                            html = html.replace(/```html\s*|```$/g, ''); // Remove markdown code block markers
                            
                            // Add basic styling if it's just a fragment
                            if (!html.includes('<html') && !html.includes('<body')) {
                              html = `
                                <div style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #333;">
                                  ${html}
                                </div>
                              `;
                            }
                            
                            // Add basic CSS for common elements if not already styled
                            if (!html.includes('<style>')) {
                              const defaultStyles = `
                                <style>
                                  body { 
                                    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                                    line-height: 1.5;
                                    color: #333;
                                    padding: 8px;
                                    margin: 0;
                                  }
                                  button, .button {
                                    background-color: ${updatedDesigns[activeDesignIndex].designSystem.colors.primary};
                                    color: white;
                                    border: none;
                                    padding: 8px 16px;
                                    border-radius: 4px;
                                    cursor: pointer;
                                  }
                                  h1, h2, h3, h4, h5, h6 {
                                    margin-top: 0.5em;
                                    margin-bottom: 0.5em;
                                  }
                                  a {
                                    color: ${updatedDesigns[activeDesignIndex].designSystem.colors.primary};
                                  }
                                  img {
                                    max-width: 100%;
                                  }
                                </style>
                              `;
                              
                              // Insert styles at appropriate location
                              if (html.includes('</head>')) {
                                html = html.replace('</head>', `${defaultStyles}</head>`);
                              } else if (html.includes('<body')) {
                                html = html.replace('<body', `${defaultStyles}<body`);
                              } else {
                                html = defaultStyles + html;
                              }
                            }
                            
                            return html;
                          }
                          return null;
                        };
                        
                        // Try to extract HTML
                        const htmlPreview = extractHtml(fullText);
                        if (htmlPreview) {
                          updatedDesigns[activeDesignIndex].htmlPreview = htmlPreview;
                        }
                        
                        // Check for specific design-related keywords in the stream
                        if (fullText.toLowerCase().includes('color') || fullText.toLowerCase().includes('palette')) {
                          // Extract color values from text if present
                          const colorRegex = /#[0-9a-f]{3,6}/gi;
                          const colors = fullText.match(colorRegex);
                          
                          if (colors && colors.length > 0) {
                            // Use actual colors from the response when available
                            if (colors.length >= 1) updatedDesigns[activeDesignIndex].designSystem.colors.primary = colors[0];
                            if (colors.length >= 2) updatedDesigns[activeDesignIndex].designSystem.colors.secondary = colors[1];
                            if (colors.length >= 3) updatedDesigns[activeDesignIndex].designSystem.colors.background = colors[2];
                            if (colors.length >= 4) updatedDesigns[activeDesignIndex].designSystem.colors.text = colors[3];
                          }
                        }
                        
                        // Update designs with new values
                        setDesigns(updatedDesigns);
                      },
                      onComplete: (content) => {
                        // Create a design artifact when streaming is complete
                        const artifactId = createDesignArtifactFromChat(content);
                        
                        // Show success notification
                        showNotification({
                          message: 'Design artifact created! See preview on the right',
                          type: 'success'
                        });
                      }
                    }}
                  />
                </Box>
              </Paper>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setActiveTab(3)}
                  startIcon={<GalleryIcon />}
                >
                  View All Design Artifacts
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveTab(0)}
                  startIcon={<DesignIcon />}
                >
                  Edit Current Design
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1">Design Preview</Typography>
                  <Chip 
                    label={designs[activeDesignIndex]?.name || 'Current Design'} 
                    size="small"
                    color="primary"
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Box 
                  sx={{ 
                    mb: 2, 
                    p: 2, 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1,
                    bgcolor: '#fafafa',
                    maxHeight: '280px',
                    overflow: 'auto'
                  }}
                >
                  {designs[activeDesignIndex]?.imageUrl ? (
                    <img 
                      src={designs[activeDesignIndex].imageUrl} 
                      alt={designs[activeDesignIndex].name}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '250px',
                        display: 'block',
                        margin: '0 auto',
                        border: '1px solid #e0e0e0'
                      }}
                    />
                  ) : designs[activeDesignIndex]?.htmlPreview ? (
                    <Box 
                      sx={{
                        height: '250px',
                        overflow: 'auto',
                        border: '1px solid #e0e0e0',
                        backgroundColor: '#fff'
                      }}
                    >
                      <div 
                        dangerouslySetInnerHTML={{ __html: designs[activeDesignIndex].htmlPreview }}
                        style={{
                          width: '100%',
                          height: '100%'
                        }}
                      />
                    </Box>
                  ) : (
                    <Box 
                      sx={{ 
                        height: '250px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        border: '1px dashed #ccc',
                        backgroundColor: '#f5f5f5',
                        padding: 2
                      }}
                    >
                      <Typography variant="body1" align="center">
                        Chat with the AI to generate an HTML design preview
                      </Typography>
                      <Typography variant="caption" align="center" sx={{ mt: 1, color: 'text.secondary' }}>
                        For example: "Create a landing page with a hero section"
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                <Typography variant="subtitle2" gutterBottom>Color Palette</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {designs[activeDesignIndex]?.designSystem?.colors && 
                   Object.entries(designs[activeDesignIndex].designSystem.colors).map(([key, value]) => (
                    <Box 
                      key={key}
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: value,
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        p: 0.5,
                        border: '1px solid #e0e0e0'
                      }}
                      title={`${key}: ${value}`}
                    />
                  ))}
                </Box>
              </Paper>
              
              <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Try These Prompts
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1,
                  maxHeight: '180px',
                  overflow: 'auto'
                }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    fullWidth
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => {
                      const textarea = document.querySelector('.MuiInputBase-input');
                      if (textarea) {
                        textarea.value = "Create HTML code for a minimal landing page design for a finance app with a clean UI. Include header, hero section, features, and call to action.";
                        textarea.dispatchEvent(new Event('input', { bubbles: true }));
                        textarea.focus();
                      }
                    }}
                  >
                    HTML for a finance app landing page
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    fullWidth
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => {
                      const textarea = document.querySelector('.MuiInputBase-input');
                      if (textarea) {
                        textarea.value = "Generate HTML code for a user profile page with dark mode. Include avatar, user info, activity feed, and settings.";
                        textarea.dispatchEvent(new Event('input', { bubbles: true }));
                        textarea.focus();
                      }
                    }}
                  >
                    HTML for a user profile page
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    fullWidth
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => {
                      const textarea = document.querySelector('.MuiInputBase-input');
                      if (textarea) {
                        textarea.value = "Write HTML and CSS for a dashboard layout with charts, stats cards, and a data table. Use placeholder images for charts.";
                        textarea.dispatchEvent(new Event('input', { bubbles: true }));
                        textarea.focus();
                      }
                    }}
                  >
                    HTML for a dashboard layout
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    fullWidth
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => {
                      const textarea = document.querySelector('.MuiInputBase-input');
                      if (textarea) {
                        textarea.value = "Create HTML and CSS for a responsive navigation menu with dropdown support and mobile hamburger menu.";
                        textarea.dispatchEvent(new Event('input', { bubbles: true }));
                        textarea.focus();
                      }
                    }}
                  >
                    HTML for a responsive navigation menu
                  </Button>
                </Box>
              </Paper>
              
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Design Tips</Typography>
                <Divider sx={{ mb: 2 }} />
              
                <Typography variant="body2" paragraph>
                  • Use a consistent color scheme throughout your application
                </Typography>
                <Typography variant="body2" paragraph>
                  • Maintain proper contrast ratios for accessibility (4.5:1 minimum)
                </Typography>
                <Typography variant="body2" paragraph>
                  • Follow a consistent spacing system for visual harmony
                </Typography>
                <Typography variant="body2" paragraph>
                  • Limit the number of fonts to 2-3 for a cleaner look
                </Typography>
                <Typography variant="body2" paragraph>
                  • Ensure interactive elements (like buttons) have proper sizing for touch targets
                </Typography>
                <Typography variant="body2" paragraph>
                  • Include visual feedback for interactive elements (hover, active states)
                </Typography>
                <Typography variant="body2" paragraph>
                  • Design with mobile-first approach when applicable
                </Typography>
                <Typography variant="body2" paragraph>
                  • Use a grid system for consistent layouts
                </Typography>
              </Paper>
              
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Resources</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body2" paragraph>
                  <LinkIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  <a href="https://material.io/design" target="_blank" rel="noopener noreferrer">
                    Material Design Guidelines
                  </a>
                </Typography>
                <Typography variant="body2" paragraph>
                  <LinkIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  <a href="https://www.figma.com/" target="_blank" rel="noopener noreferrer">
                    Figma Design Tool
                  </a>
                </Typography>
                <Typography variant="body2" paragraph>
                  <LinkIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  <a href="https://coolors.co/" target="_blank" rel="noopener noreferrer">
                    Coolors Color Palette Generator
                  </a>
                </Typography>
                <Typography variant="body2" paragraph>
                  <LinkIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  <a href="https://type-scale.com/" target="_blank" rel="noopener noreferrer">
                    Type Scale Tool
                  </a>
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {uploadType === 'link' ? 'Add Design Link' : 
           uploadType === 'pdf' ? 'Upload PDF Document' : 
           uploadType === 'image' ? 'Upload Design Image' : 'Add Design Artifact'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={artifactTitle}
                onChange={(e) => setArtifactTitle(e.target.value)}
                placeholder="Enter a title for this design artifact"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={artifactDescription}
                onChange={(e) => setArtifactDescription(e.target.value)}
                placeholder="Enter a short description"
                multiline
                rows={2}
              />
            </Grid>
            
            {uploadType === 'link' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Design Link"
                  value={designLink}
                  onChange={(e) => setDesignLink(e.target.value)}
                  placeholder="Enter URL to design (Figma, Adobe XD, etc.)"
                  helperText="Enter a link to your design on Figma, Adobe XD, or other design platforms"
                />
              </Grid>
            )}
            
            {(uploadType === 'pdf' || uploadType === 'image') && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    mb: 2
                  }}
                >
                  <input
                    type="file"
                    id="design-file-upload"
                    accept={uploadType === 'pdf' ? "application/pdf" : "image/*"}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={(e) => {
                      // Display the selected file name
                      if (e.target.files[0]) {
                        setArtifactTitle(artifactTitle || e.target.files[0].name);
                      }
                    }}
                  />
                  <label htmlFor="design-file-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<UploadIcon />}
                      onClick={() => fileInputRef.current.click()}
                    >
                      Choose File
                    </Button>
                  </label>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {fileInputRef.current && fileInputRef.current.files && fileInputRef.current.files[0] ? 
                      `Selected: ${fileInputRef.current.files[0].name}` : 
                      'No file selected'}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>Cancel</Button>
          <Button 
            variant="contained"
            onClick={handleUploadArtifact}
            disabled={(uploadType === 'link' && !designLink) || 
                     ((uploadType === 'pdf' || uploadType === 'image') && 
                      !(fileInputRef.current && fileInputRef.current.files && fileInputRef.current.files[0]))}
          >
            {uploadType === 'link' ? 'Add Link' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Content Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{dialogData.title || 'Design Content'}</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            {dialogData.content && (
              <Typography 
                component="div" 
                variant="body1" 
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {dialogData.content}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Designs;